import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { TileCategory, TileData } from '../../shared/types';
import { clientHeaders } from '../clientIdentity';
import { Icon } from './Icon';

const labels: Record<TileCategory, string> = {
  floor: 'Половая плитка',
  darkWall: 'Стеновая тёмная',
  lightWall: 'Стеновая светлая',
  decor: 'Декор',
  border: 'Фриз',
};

interface TileUploadCardProps {
  category: TileCategory;
  tile: TileData | null;
  selected: boolean;
  onSelect: () => void;
  onUploaded: (tile: TileData) => void;
}

export const TileUploadCard = ({
  category,
  tile,
  selected,
  onSelect,
  onUploaded,
}: TileUploadCardProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  const upload = async (file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Нужен JPG, PNG или WEBP');
      return;
    }

    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', category);

    try {
      const response = await fetch('/api/tiles', {
        method: 'POST',
        headers: clientHeaders(),
        body: formData,
      });
      const payload = (await response.json()) as { tile?: Partial<TileData>; error?: string };
      if (!response.ok || !payload.tile?.imagePath) throw new Error(payload.error ?? 'Ошибка загрузки');
      onUploaded({
        id: payload.tile.id ?? crypto.randomUUID(),
        category,
        name: file.name.replace(/\.[^.]+$/, ''),
        sku: '',
        imagePath: payload.tile.imagePath,
        originalWidth: payload.tile.originalWidth ?? 800,
        originalHeight: payload.tile.originalHeight ?? 800,
        tileWidth: tile?.tileWidth ?? (category === 'border' ? 600 : 300),
        tileHeight: tile?.tileHeight ?? (category === 'border' ? 100 : 600),
        groutWidth: tile?.groutWidth ?? 3,
        rotation: 0,
        scale: 1,
        mirror: false,
        variationEnabled: true,
        price: tile?.price ?? 0,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Ошибка загрузки');
    } finally {
      setUploading(false);
    }
  };

  const onInput = (event: ChangeEvent<HTMLInputElement>) => {
    void upload(event.target.files?.[0]);
    event.target.value = '';
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void upload(event.dataTransfer.files[0]);
  };

  return (
    <div
      className={`tile-upload-card ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
      onDragOver={(event) => event.preventDefault()}
      onDragEnter={() => setDragging(true)}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <button
        type="button"
        className="tile-card-select"
        aria-pressed={selected}
        onClick={onSelect}
      >
        <span className="tile-thumb">
          {tile ? <img src={tile.imagePath} alt="" /> : <Icon name="image" />}
          {uploading && <span className="upload-progress" />}
        </span>
        <span className="tile-card-copy">
          <strong>{labels[category]}</strong>
          <small>{tile ? `${tile.tileWidth} × ${tile.tileHeight} мм` : 'Фото не выбрано'}</small>
        </span>
      </button>
      <div className="tile-upload-actions">
        <button
          type="button"
          className="tile-photo-button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Icon name="image" />
          {uploading ? 'Загрузка…' : tile ? 'Заменить фото' : 'Выбрать фото'}
        </button>
        <button
          type="button"
          className="tile-photo-button tile-camera-button"
          disabled={uploading}
          onClick={() => cameraRef.current?.click()}
          title="Сфотографировать плитку камерой"
        >
          <Icon name="camera" />
          Сфотографировать
        </button>
      </div>
      <small className={`tile-upload-help ${error ? 'error-text' : ''}`}>
        {dragging ? 'Отпустите файл здесь' : error || 'JPG, PNG или WEBP · до 50 МБ'}
      </small>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onInput} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onInput} />
    </div>
  );
};
